import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgxSpinner, NgxSpinnerService, NgxSpinnerComponent } from 'ngx-spinner';
import { Observable, Subject, take, takeUntil } from 'rxjs';
import { POOL_CATEGORY_OPTION, POOL_SORT_OPTION, POOL_VENDOR_OPTION } from '../_shared/_constants/local-storage-keys';
import { SORT_OPTIONS } from '../_shared/_constants/sort-options';
import { ProductCategories } from '../_shared/_enums/product-categories';
import { IShopifyProduct, IProductType, IVendor, SEO } from '@app/_shared/_models';
import { ChemicalService } from '../_shared/_services/chemical.service';
import { CollectionService } from '../_shared/_services/collection.service';
import { PrismicService } from '../_shared/_services/prismic.service';
import { ProductTypeService } from '../_shared/_services/product-type.service';
import { ProductService } from '../_shared/_services/product.service';
import { SeoService } from '../_shared/_services/seo.service';
import { VendorService } from '../_shared/_services/vendor.service';
import { safeSessionStorageGetItem, safeSessionStorageSetItem, safeSessionStorageRemoveItem } from '../_shared/_helpers/platform.helpers';
import { IPoolCarePage } from './_prismic/pool-care';
import { ImageComponent } from '../_shared/_components/image/image.component';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { ProductCardComponent } from '../_shared/_components/product-card/product-card.component';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-pool-care',
    templateUrl: './pool-care.component.html',
    styleUrls: ['./pool-care.component.scss'],
    imports: [ImageComponent, MatFormField, MatSelect, FormsModule, ReactiveFormsModule, MatOption, NgxSpinnerComponent, ProductCardComponent, AsyncPipe]
})
export class PoolCareComponent implements OnInit, OnDestroy {
  private readonly prismicService = inject(PrismicService);
  private readonly vendorService = inject(VendorService);
  private readonly chemicalService = inject(ChemicalService);
  private readonly productTypeService = inject(ProductTypeService);
  private readonly collectionService = inject(CollectionService);
  private readonly productService = inject(ProductService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly seoService = inject(SeoService);
  private readonly ngxSpinnerService = inject(NgxSpinnerService);

  public seo: SEO = {
    image: 'https://mypoolhut.com/assets/images/logos/main_logo.png',
    metaDescription: 'Shop pool care supplies at My Pool Hut.  Chemicals and treatments to keep your pool clean and refreshing!',
    metaTitle: 'My Pool Hut - Pool Care Supplies'
  };
  public poolCarePage!: IPoolCarePage;
  public products$!: Observable<Array<IShopifyProduct>>;
  public spinner$!: Observable<NgxSpinner>;
  public allProducts = new Array<IShopifyProduct>();
  public filteredProducts = new Array<IShopifyProduct>();
  public vendors = new Array<IVendor>();
  public vendorsControl = new UntypedFormControl();
  public categories = new Array<IProductType>();
  public categoriesControl = new UntypedFormControl();
  public sortOptions = SORT_OPTIONS;
  public sortControl = new UntypedFormControl();
  public selectedSortOption = this.sortOptions[0];
  public selectedVendorOption = new Array<IVendor>();
  public selectedCategoryOption = new Array<IProductType>();

  private readonly unsubscribe: Subject<undefined> = new Subject<undefined>();

  public async ngOnInit(): Promise<void> {
    this.spinner$ = this.ngxSpinnerService.spinnerObservable;
    await this.ngxSpinnerService.show();
    this.seoService.setMetaData(this.seo);
    this.seoService.createCanonicalUrl('https://mypoolhut.com/pool-care');

    this.vendors = this.vendorService.vendors.filter(v => v.category === ProductCategories.BOTH || v.category === ProductCategories.POOL);
    this.collectionService.loadProducts('pool-care-supplies');

    this.categories = this.productTypeService.productTypes;
    this.products$ = this.collectionService.products$;

    this.prismicService.getSingle('pool_care')
      .pipe(take(1))
      .subscribe((res: any) => {
        this.poolCarePage = {
          hero_image: res.data.hero_image,
          title: res.data.main_title
        }
      });

    this.products$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(async (products: Array<IShopifyProduct>) => {
        if (products?.length > 0) {
          this.allProducts = ProductService.sortProducts(products, this.selectedSortOption);
          this.filteredProducts = ProductService.sortProducts(products, this.selectedSortOption);

          this.categoriesControl.valueChanges
            .pipe(takeUntil(this.unsubscribe))            .subscribe((categories: Array<IProductType>) => {
              if (categories?.length < 1) {
                safeSessionStorageRemoveItem(POOL_CATEGORY_OPTION);
                this.filteredProducts = ProductService.filterByVendors(this.allProducts, this.vendorsControl.value);
              } else {
                safeSessionStorageSetItem(POOL_CATEGORY_OPTION, JSON.stringify(categories));
                this.filteredProducts = ProductService.filterByVendors(this.allProducts, this.vendorsControl.value);
                this.filteredProducts = ProductService.filterByProductType(this.filteredProducts, categories);
              }
              this.filteredProducts = ProductService.sortProducts(this.filteredProducts, this.sortControl.value);
            });        this.sortControl.valueChanges
          .pipe(takeUntil(this.unsubscribe))
          .subscribe((sortOption: string) => {
            safeSessionStorageSetItem(POOL_SORT_OPTION, sortOption)
            this.filteredProducts = ProductService.sortProducts(this.filteredProducts, sortOption);
          });

        this.vendorsControl.valueChanges
          .pipe(takeUntil(this.unsubscribe))          .subscribe((vendors: Array<IVendor>) => {
            if (vendors?.length < 1) {
              safeSessionStorageRemoveItem(POOL_VENDOR_OPTION);
              this.filteredProducts = ProductService.filterByProductType(this.allProducts, this.categoriesControl.value);
            } else {
              safeSessionStorageSetItem(POOL_VENDOR_OPTION, JSON.stringify(vendors));
              this.filteredProducts = ProductService.filterByProductType(this.allProducts, this.categoriesControl.value);
              this.filteredProducts = ProductService.filterByVendors(this.filteredProducts, vendors);
            }
            this.filteredProducts = ProductService.sortProducts(this.filteredProducts, this.sortControl.value);
          });

        this.activatedRoute.queryParams
          .pipe(takeUntil(this.unsubscribe))
          .subscribe((params: any) => {
            const vendorName = params['brands'];
            const categories = params['categories'];
            if (vendorName) {
              const vendor = this.vendors.find(v => v.name.toLowerCase() === vendorName);
              if (vendor) {
                this.vendorsControl.setValue([vendor]);
              }
            }

            if (categories) {
              const category = this.categories.find(c => c.label.toLowerCase() == categories);
              if (category) {
                this.categoriesControl.setValue([category]);
              }
            }
          });

          this.loadFiltersFromsessionStorage();
          await this.ngxSpinnerService.hide();
        }
      });
  }

  ngOnDestroy(): void {
    this.seoService.removeCanonicalUrl();
    this.unsubscribe.next(undefined);
    this.unsubscribe.complete();
  }

  public compareVendors(object1: IVendor, object2: IVendor): boolean {
    return object1 && object2 && object1.name == object2.name;
  }

  public compareCategories(object1: IProductType, object2: IProductType): boolean {
    return object1 && object2 && object1.label == object2.label;
  }

  public clearFilters(): void {
    this.categoriesControl.setValue([]);
    this.vendorsControl.setValue([]);
  }

  private loadFiltersFromsessionStorage(): void {    // Get Session Storage Filter Options
    // Sort By
    this.selectedSortOption = safeSessionStorageGetItem(POOL_SORT_OPTION) ?? this.sortOptions[0];
    this.sortControl.setValue(this.selectedSortOption);

    // Filter By Brand
    this.selectedVendorOption = JSON.parse(safeSessionStorageGetItem(POOL_VENDOR_OPTION) ?? '[]') as Array<IVendor>;
    if (this.selectedVendorOption.length > 0) {
      this.vendorsControl.setValue(this.selectedVendorOption);
    }

    // Filter By Category
    this.selectedCategoryOption = JSON.parse(safeSessionStorageGetItem(POOL_CATEGORY_OPTION) ?? '[]') as Array<IProductType>;
    if (this.selectedCategoryOption.length > 0) {
      this.categoriesControl.setValue(this.selectedCategoryOption);
    }
  }
}
